import { Money } from '../ui/Money';
import React, { useState } from 'react';
import { Edit, Trash2, Banknote, FileDown, FileSpreadsheet, UserX, TrendingUp, AlertOctagon, AlertTriangle, User, Clock, CreditCard, CalendarX, Eye, EyeOff, ShieldOff, ShieldCheck, CalendarDays, Mail, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../ui/dropdown-menu';
import { format, differenceInYears, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate age
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  return differenceInYears(new Date(), new Date(dateOfBirth));
};

// Helper function to check probation status
const getProbationStatus = (joinDate) => {
  if (!joinDate) return null;
  const join = new Date(joinDate);
  const today = new Date();
  const probationEnd = new Date(join);
  probationEnd.setDate(probationEnd.getDate() + 180);
  
  if (today < probationEnd) {
    const daysRemaining = differenceInDays(probationEnd, today);
    const daysWorked = differenceInDays(today, join);
    return {
      isOnProbation: true,
      daysRemaining,
      daysWorked,
      probationEndDate: probationEnd
    };
  }
  return { isOnProbation: false };
};

// Helper function to check notice period (resigned/terminated but still working)
const getNoticePeriodStatus = (employee) => {
  const status = employee.employment_status;
  if (!['Resigned', 'Terminated'].includes(status)) return null;
  
  const lastDateStr = employee.last_working_date || employee.resignation_date || employee.termination_date;
  if (!lastDateStr) return null;
  
  try {
    const lastDate = new Date(lastDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastDate.setHours(0, 0, 0, 0);
    
    if (lastDate >= today) {
      const daysRemaining = differenceInDays(lastDate, today);
      return {
        isInNoticePeriod: true,
        daysRemaining,
        lastWorkingDate: lastDate,
        status
      };
    }
  } catch (e) {
    return null;
  }
  return null;
};

const EmployeeCard = ({
  employee,
  isViewer,
  onEdit,
  onDelete,
  onPromotion,
  onSuspension,
  onAdvances,
  onWarnings,
  onEOSB,
  onLeaveSettlement,
  onDownloadStaffPDF,
  onDownload,
  onTogglePortal,
  onMeetingEmail,
  onReinstate,
  allEmployees = []
}) => {
  const isInactive = ['Resigned', 'Terminated'].includes(employee.employment_status);
  const noticePeriod = getNoticePeriodStatus(employee);
  const isInNoticePeriod = noticePeriod?.isInNoticePeriod;
  const probation = getProbationStatus(employee.join_date);
  const age = calculateAge(employee.date_of_birth);
  const [showSalary, setShowSalary] = useState(false);
  const reportingManagers = (employee.reporting_manager_ids || [])
    .map(id => allEmployees.find(e => e.id === id))
    .filter(Boolean);

  // Only show as inactive if they're resigned/terminated AND their last day has passed
  const showAsInactive = isInactive && !isInNoticePeriod;

  return (
    <Card className={`hover:shadow-lg transition-shadow ${showAsInactive ? 'opacity-70 bg-gray-50' : ''} ${isInNoticePeriod ? 'border-l-4 border-l-orange-500' : ''}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-3">
            {/* Employee Photo */}
            <div className={`w-12 h-12 rounded-full border-2 overflow-hidden flex items-center justify-center flex-shrink-0 ${showAsInactive ? 'border-gray-300 bg-gray-200' : 'border-gray-200 bg-gray-100'}`}>
              {employee.photo_url ? (
                <img 
                  src={`${API_URL}${employee.photo_url}`} 
                  alt={employee.name}
                  className={`w-full h-full object-cover ${showAsInactive ? 'grayscale' : ''}`}
                  data-testid={`employee-photo-${employee.id}`}
                />
              ) : (
                <User className={`w-6 h-6 ${showAsInactive ? 'text-gray-400' : 'text-gray-400'}`} />
              )}
            </div>
            <div>
              <CardTitle className={`text-lg ${showAsInactive ? 'text-gray-500' : ''}`}>{employee.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-gray-600">{employee.position}</p>
                {employee.employee_role && employee.employee_role !== 'Staff' && (
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${
                    employee.employee_role === 'Owner' ? 'border-amber-400 text-amber-700 bg-amber-50' :
                    employee.employee_role === 'Top Management' ? 'border-purple-400 text-purple-700 bg-purple-50' :
                    employee.employee_role === 'Management' ? 'border-indigo-400 text-indigo-700 bg-indigo-50' :
                    'border-blue-300 text-blue-600'
                  }`} data-testid={`role-badge-${employee.id}`}>{employee.employee_role}</Badge>
                )}
                {employee.portal_active === false && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-red-300 text-red-600 bg-red-50" data-testid={`portal-off-badge-${employee.id}`}>
                    <ShieldOff className="w-2.5 h-2.5 mr-0.5" />Portal Off
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 justify-end max-w-[120px]">
            {!isViewer && (
              <>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEdit(employee)} data-testid={`edit-employee-${employee.id}`} title="Edit Employee">
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onPromotion(employee)} title="View/Add Promotions">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onSuspension(employee)} title="Manage Suspensions">
                  <AlertOctagon className="h-4 w-4 text-orange-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 relative" onClick={() => onWarnings(employee)} title="Warning Letters" data-testid={`warnings-employee-${employee.id}`}>
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  {(employee.warning_letters?.filter(w => {
                    const exp = new Date(w.issue_date);
                    exp.setMonth(exp.getMonth() + 6);
                    return exp > new Date();
                  }).length || 0) > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {employee.warning_letters.filter(w => {
                        const exp = new Date(w.issue_date);
                        exp.setMonth(exp.getMonth() + 6);
                        return exp > new Date();
                      }).length}
                    </span>
                  )}
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onAdvances(employee)} title="Salary Advances">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                </Button>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onMeetingEmail && onMeetingEmail(employee)} title="Invite to meeting (email staff)" data-testid={`meeting-email-employee-${employee.id}`}>
                  <Mail className="h-4 w-4 text-indigo-600" />
                </Button>
                {/* Portal Access Toggle - visible standalone button */}
                <Button
                  size="sm"
                  variant="ghost"
                  className={`h-8 w-8 p-0 ${employee.portal_active === false ? 'bg-red-50' : ''}`}
                  onClick={() => onTogglePortal && onTogglePortal(employee, employee.portal_active === false)}
                  title={employee.portal_active === false ? 'Activate Portal Access' : 'Deactivate Portal Access'}
                  data-testid={`toggle-portal-btn-${employee.id}`}
                >
                  {employee.portal_active === false ? (
                    <ShieldOff className="h-4 w-4 text-red-500" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 text-green-500" />
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>Manage Access</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {employee.portal_active === false ? (
                      <DropdownMenuItem onClick={() => onTogglePortal && onTogglePortal(employee, true)} data-testid={`activate-portal-${employee.id}`}>
                        <ShieldCheck className="h-4 w-4 mr-2 text-green-600" />
                        Activate Portal
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => onTogglePortal && onTogglePortal(employee, false)} data-testid={`deactivate-portal-${employee.id}`}>
                        <ShieldOff className="h-4 w-4 mr-2 text-orange-600" />
                        Deactivate Portal
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {(isInactive || employee.is_active === false) && (
                      <>
                        <DropdownMenuItem onClick={() => onReinstate && onReinstate(employee)} data-testid={`reinstate-employee-${employee.id}`}>
                          <RotateCcw className="h-4 w-4 mr-2 text-green-600" />
                          Reinstate (rehire)
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuLabel>Delete Options</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onDelete(employee.id, false)}>
                      <UserX className="h-4 w-4 mr-2" />
                      Deactivate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(employee.id, true)} className="text-red-600 focus:text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Permanently Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-600">Employee Code</p>
            <p className="text-xs font-mono font-semibold text-purple-600">{employee.employee_code}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Age</p>
            {age !== null ? (
              <p className={`text-sm font-semibold ${age >= 60 ? 'text-orange-600' : 'text-gray-800'}`}>
                {age} years {age >= 60 && <span className="text-xs">(60+)</span>}
              </p>
            ) : (
              <p className="text-sm text-gray-400">N/A</p>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-gray-600">Department</p>
          <Badge variant="secondary">{employee.department}</Badge>
        </div>
        {reportingManagers.length > 0 && (
          <div data-testid={`reporting-manager-${employee.id}`}>
            <p className="text-sm text-gray-600">Reporting Manager{reportingManagers.length > 1 ? 's' : ''}</p>
            {reportingManagers.map(mgr => (
              <p key={mgr.id} className="text-sm font-medium">{mgr.name}</p>
            ))}
          </div>
        )}
        
        {/* Probation Status */}
        {probation?.isOnProbation && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">On Probation</span>
              </div>
              <Badge className="bg-amber-500">{probation.daysRemaining}d left</Badge>
            </div>
            <p className="text-xs text-amber-600 mt-1">
              {probation.daysWorked} days worked • Ends {format(probation.probationEndDate, 'dd MMM yyyy')}
            </p>
          </div>
        )}

        {/* Notice Period Status */}
        {isInNoticePeriod && (
          <div className="bg-orange-50 border border-orange-300 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarX className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-700">
                  {noticePeriod.status === 'Resigned' ? 'Resignation Notice' : 'Termination Notice'}
                </span>
              </div>
              <Badge className="bg-orange-500">{noticePeriod.daysRemaining}d left</Badge>
            </div>
            <p className="text-xs text-orange-600 mt-1">
              Last working day: {format(noticePeriod.lastWorkingDate, 'dd MMM yyyy')}
            </p>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <Badge variant={employee.employment_status === 'Active' ? 'default' : 'outline'}>
              {employee.employment_status}
            </Badge>
          </div>
          {(employee.lop_days > 0 || employee.lop_days_ytd > 0) && (
            <div className="text-right" data-testid={`lop-summary-${employee.id}`}>
              <p className="text-sm text-gray-600">LOP Days</p>
              <div className="flex items-center gap-1.5 justify-end">
                {employee.lop_days_ytd > 0 && (
                  <Badge className="bg-red-100 text-red-700 border border-red-200" title={`${new Date().getFullYear()} year-to-date Loss of Pay`} data-testid={`lop-ytd-${employee.id}`}>
                    {Number(employee.lop_days_ytd).toFixed(2)} <span className="ml-1 font-normal opacity-70">YTD</span>
                  </Badge>
                )}
                {employee.lop_days > 0 && (
                  <Badge variant="destructive" title="Total Loss of Pay (all-time)" data-testid={`lop-total-${employee.id}`}>
                    {Number(employee.lop_days).toFixed(2)} <span className="ml-1 font-normal opacity-80">total</span>
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Total Salary</p>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
              onClick={() => setShowSalary(!showSalary)}
              data-testid={`toggle-salary-${employee.id}`}
              title={showSalary ? 'Hide salary' : 'Show salary'}
            >
              {showSalary ? <EyeOff className="h-3.5 w-3.5 text-gray-500" /> : <Eye className="h-3.5 w-3.5 text-gray-500" />}
            </Button>
          </div>
          {showSalary ? (
            <>
              <p className="text-lg font-bold text-green-600">
                <Money value={(employee.salary?.basic || 0) + (employee.salary?.housing || 0) +
                (employee.salary?.transportation || 0) + (employee.salary?.other || 0)} />
              </p>
              <p className="text-xs text-gray-500">
                Basic: {employee.salary?.basic || 0} | Housing: {employee.salary?.housing || 0}
              </p>
            </>
          ) : (
            <p className="text-lg font-bold text-gray-400 tracking-widest" data-testid={`salary-hidden-${employee.id}`}>*****</p>
          )}
        </div>
        <div>
          <p className="text-sm text-gray-600">Email</p>
          <p className="text-sm">{employee.email}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Join Date</p>
          <p className="text-sm">{format(new Date(employee.join_date), 'MMM dd, yyyy')}</p>
        </div>
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-600">Payment Method</p>
            <p className="text-sm">{employee.payment_method || 'Not Set'}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Visa Company</p>
            <p className="text-sm font-medium">{employee.visa_company || 'N/A'}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1" onClick={() => onEOSB(employee)}>
            <Banknote className="h-4 w-4 mr-1" />
            EOSB
          </Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={() => onLeaveSettlement(employee)} data-testid={`leave-settlement-btn-${employee.employee_code}`}>
            <CalendarDays className="h-4 w-4 mr-1" />
            Leave Salary
          </Button>
          <Button size="sm" variant="outline" className="flex-1" onClick={() => onDownloadStaffPDF(employee.id, employee.name)}>
            <FileDown className="h-4 w-4 mr-1" />
            Staff PDF
          </Button>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="w-full mt-2">
              <FileDown className="h-4 w-4 mr-2" />
              Download Documents
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuLabel>Leave History</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onDownload(employee.id, 'leave-history', 'pdf')}>
              <FileDown className="h-4 w-4 mr-2" />
              Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDownload(employee.id, 'leave-history', 'excel')}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Download Excel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  );
};

export default EmployeeCard;
